import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft, Save, Loader2, Plus, Trash2, Edit, ExternalLink,
  Mail, MessageSquare, Home, Globe, Video, Zap, Droplets, Flame,
  Wifi, AlertTriangle, CheckCircle, List, FileText, Send
} from "lucide-react";
import { API } from "@/App";

// Portal configurations
const PORTALS = [
  { id: "rightmove", name: "Rightmove", logo: "🏠", color: "bg-green-100 text-green-700" },
  { id: "zoopla", name: "Zoopla", logo: "🔍", color: "bg-purple-100 text-purple-700" },
  { id: "onthemarket", name: "OnTheMarket", logo: "📍", color: "bg-blue-100 text-blue-700" },
];

const MARKETING_ALERT_TYPES = [
  { id: "back_on_market", label: "Back on Market", description: "Send marketing alerts for a property coming back onto the market" },
  { id: "new_property", label: "New Property", description: "Send marketing alerts for a new property coming onto the market" },
  { id: "new_property_match", label: "New property for you to buy", description: "Send marketing alerts to matching applicants for this property" },
  { id: "price_change", label: "Price Change", description: "Send marketing alerts for a price change" },
  { id: "summary_details", label: "Summary Details", description: "Send a general marketing alert advertising this property" },
];

export const MarketingDetailsPage = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [caseData, setCaseData] = useState(null);

  // Marketing Details State
  const [marketing, setMarketing] = useState({
    on_market: false,
    summary: "",
    full_description: "",
    headline: "",
    marketing_tag: "",
    virtual_tour_link: "",
    virtual_tour_link_2: "",
    website_link: "",
    rightmove_link: "",
    zoopla_link: "",
    onthemarket_link: "",
  });

  // Features State
  const [features, setFeatures] = useState([]);
  const [newFeature, setNewFeature] = useState("");
  const [restrictions, setRestrictions] = useState([]);
  const [newRestriction, setNewRestriction] = useState("");

  // Utilities State
  const [utilities, setUtilities] = useState({
    electricity_supply: "",
    heating: "",
    water_supply: "",
    sewerage: "",
    broadband: "",
  });

  // Portal Accounts State
  const [portalAccounts, setPortalAccounts] = useState([
    { portal: "rightmove", account_id: "", account_name: "Default", active: false, link: "" },
    { portal: "zoopla", account_id: "", account_name: "Realtime", active: false, link: "" },
    { portal: "onthemarket", account_id: "", account_name: "Default", active: false, link: "" },
  ]);

  useEffect(() => {
    fetchData();
  }, [caseId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/sales/cases/${caseId}`, { headers });
      const caseInfo = res.data.case;
      setCaseData(caseInfo);

      // Load marketing data
      setMarketing({
        on_market: caseInfo.on_market || false,
        summary: caseInfo.summary || "",
        full_description: caseInfo.full_description || caseInfo.description || "",
        headline: caseInfo.headline || "",
        marketing_tag: caseInfo.marketing_tag || "",
        virtual_tour_link: caseInfo.virtual_tour_link || "",
        virtual_tour_link_2: caseInfo.virtual_tour_link_2 || "",
        website_link: caseInfo.website_link || "",
        rightmove_link: caseInfo.rightmove_link || "",
        zoopla_link: caseInfo.zoopla_link || "",
        onthemarket_link: caseInfo.onthemarket_link || "",
      });

      // Load features
      setFeatures(caseInfo.features || []);
      setRestrictions(caseInfo.restrictions || []);

      // Load utilities
      setUtilities({
        electricity_supply: caseInfo.electricity_supply || "",
        heating: caseInfo.heating || "",
        water_supply: caseInfo.water_supply || "",
        sewerage: caseInfo.sewerage || "",
        broadband: caseInfo.broadband || "",
      });

      // Load portal accounts
      if (caseInfo.portal_accounts) {
        setPortalAccounts(caseInfo.portal_accounts);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load marketing details");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/sales/cases/${caseId}/marketing`, {
        ...marketing,
        features,
        restrictions,
        utilities,
        portal_accounts: portalAccounts,
      }, { headers });
      toast.success("Marketing details saved!");
    } catch (error) {
      toast.error("Failed to save marketing details");
    } finally {
      setSaving(false);
    }
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      setFeatures([...features, newFeature.trim()]);
      setNewFeature("");
    }
  };

  const removeFeature = (index) => {
    setFeatures(features.filter((_, i) => i !== index));
  };

  const addRestriction = () => {
    if (newRestriction.trim()) {
      setRestrictions([...restrictions, newRestriction.trim()]);
      setNewRestriction("");
    }
  };

  const removeRestriction = (index) => {
    setRestrictions(restrictions.filter((_, i) => i !== index));
  };

  const handlePortalUpdate = (portalId) => {
    toast.success(`Updating ${portalId}...`);
    // In real implementation, this would trigger an API call to update the portal listing
  };

  const handlePortalRemove = (portalId) => {
    toast.success(`Removing from ${portalId}...`);
    // In real implementation, this would trigger an API call to remove from portal
  };

  const handleSendAlert = (alertType, method) => {
    toast.success(`Sending ${alertType} via ${method}...`);
    // In real implementation, this would trigger the marketing alert
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate(`/sales/case/${caseId}/documents`)}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-semibold">Marketing Details</h1>
                <p className="text-sm text-muted-foreground">
                  {caseData?.address || caseData?.property_address}
                </p>
              </div>
            </div>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save All Changes
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-3 space-y-6">
            {/* Marketing Details Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Marketing Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* On Market Toggle */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <Label className="text-base font-medium">On the Market</Label>
                    <p className="text-sm text-muted-foreground">Property is actively listed for sale</p>
                  </div>
                  <Switch
                    checked={marketing.on_market}
                    onCheckedChange={(checked) => setMarketing({ ...marketing, on_market: checked })}
                  />
                </div>

                {/* Summary - 300 characters max */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-sm font-medium">Summary</Label>
                    <span className={`text-xs ${marketing.summary.length > 300 ? 'text-red-500 font-medium' : 'text-muted-foreground'}`}>
                      {marketing.summary.length}/300 characters
                    </span>
                  </div>
                  <Textarea
                    placeholder="Brief summary for portal search results (max 300 characters)..."
                    value={marketing.summary}
                    onChange={(e) => {
                      if (e.target.value.length <= 300) {
                        setMarketing({ ...marketing, summary: e.target.value });
                      }
                    }}
                    maxLength={300}
                    className="min-h-[100px]"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    This appears in portal search results. Keep it punchy and highlight key selling points.
                  </p>
                </div>

                {/* Full Description - Rightmove */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-sm font-medium">Full Description (Rightmove)</Label>
                    <span className="text-xs text-muted-foreground">
                      {marketing.full_description.length} characters
                    </span>
                  </div>
                  <Textarea
                    placeholder="Full property description for Rightmove, Zoopla & OnTheMarket...

Include details about:
• Each room and its features
• Garden and outdoor spaces
• Location benefits (schools, transport, shops)
• Recent improvements or upgrades
• Any unique selling points"
                    value={marketing.full_description}
                    onChange={(e) => setMarketing({ ...marketing, full_description: e.target.value })}
                    className="min-h-[300px]"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    The complete property description that appears on portal listing pages. Be detailed and descriptive.
                  </p>
                </div>

                {/* Headline & Marketing Tag */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium mb-2 block">Headline</Label>
                    <Input
                      placeholder="e.g. Stunning Family Home"
                      value={marketing.headline}
                      onChange={(e) => setMarketing({ ...marketing, headline: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium mb-2 block">Marketing Tag</Label>
                    <Input
                      placeholder="e.g. Chain Free, Must See"
                      value={marketing.marketing_tag}
                      onChange={(e) => setMarketing({ ...marketing, marketing_tag: e.target.value })}
                    />
                  </div>
                </div>

                {/* Virtual Tour Links */}
                <div className="space-y-3">
                  <Label className="text-sm font-medium block">Virtual Tour Links</Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Input
                        placeholder="Virtual tour link"
                        value={marketing.virtual_tour_link}
                        onChange={(e) => setMarketing({ ...marketing, virtual_tour_link: e.target.value })}
                      />
                    </div>
                    <div>
                      <Input
                        placeholder="Virtual tour link 2"
                        value={marketing.virtual_tour_link_2}
                        onChange={(e) => setMarketing({ ...marketing, virtual_tour_link_2: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

                {/* Website Link */}
                <div>
                  <Label className="text-sm font-medium mb-2 block">Link on Website</Label>
                  <Input
                    placeholder="https://yourwebsite.com/properties/..."
                    value={marketing.website_link}
                    onChange={(e) => setMarketing({ ...marketing, website_link: e.target.value })}
                  />
                </div>

                {/* Portal Links */}
                <div className="space-y-3">
                  <Label className="text-sm font-medium block">Portal Links</Label>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm w-32 font-medium">Rightmove:</span>
                      <Input
                        placeholder="https://www.rightmove.com/properties/..."
                        value={marketing.rightmove_link}
                        onChange={(e) => setMarketing({ ...marketing, rightmove_link: e.target.value })}
                        className="flex-1"
                      />
                      {marketing.rightmove_link && (
                        <Button variant="outline" size="sm" asChild>
                          <a href={marketing.rightmove_link} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        </Button>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm w-32 font-medium">Zoopla:</span>
                      <Input
                        placeholder="https://www.zoopla.co.uk/..."
                        value={marketing.zoopla_link}
                        onChange={(e) => setMarketing({ ...marketing, zoopla_link: e.target.value })}
                        className="flex-1"
                      />
                      {marketing.zoopla_link && (
                        <Button variant="outline" size="sm" asChild>
                          <a href={marketing.zoopla_link} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        </Button>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm w-32 font-medium">OnTheMarket:</span>
                      <Input
                        placeholder="https://www.onthemarket.com/details/..."
                        value={marketing.onthemarket_link}
                        onChange={(e) => setMarketing({ ...marketing, onthemarket_link: e.target.value })}
                        className="flex-1"
                      />
                      {marketing.onthemarket_link && (
                        <Button variant="outline" size="sm" asChild>
                          <a href={marketing.onthemarket_link} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Key Features Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <List className="w-5 h-5" />
                  Features & Restrictions
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Features */}
                <div>
                  <Label className="text-sm font-medium mb-3 block">Key Features</Label>
                  <div className="flex gap-2 mb-3">
                    <Input
                      placeholder="Add a feature (e.g. 4 Bedroom detached)"
                      value={newFeature}
                      onChange={(e) => setNewFeature(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && addFeature()}
                    />
                    <Button onClick={addFeature}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  {features.length > 0 ? (
                    <ul className="space-y-2">
                      {features.map((feature, index) => (
                        <li key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <span className="flex items-center gap-2">
                            <span className="text-purple-600">•</span>
                            {feature}
                          </span>
                          <Button variant="ghost" size="sm" onClick={() => removeFeature(index)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-muted-foreground italic p-4 bg-amber-50 rounded-lg text-amber-700">
                      No features added yet. Add key selling points for portal listings.
                    </p>
                  )}
                </div>

                {/* Restrictions */}
                <div>
                  <Label className="text-sm font-medium mb-3 block">Restrictions</Label>
                  <div className="flex gap-2 mb-3">
                    <Input
                      placeholder="Add a restriction (e.g. No pets allowed)"
                      value={newRestriction}
                      onChange={(e) => setNewRestriction(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && addRestriction()}
                    />
                    <Button onClick={addRestriction} variant="outline">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  {restrictions.length > 0 ? (
                    <ul className="space-y-2">
                      {restrictions.map((restriction, index) => (
                        <li key={index} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                          <span className="flex items-center gap-2 text-red-700">
                            <AlertTriangle className="w-4 h-4" />
                            {restriction}
                          </span>
                          <Button variant="ghost" size="sm" onClick={() => removeRestriction(index)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-muted-foreground italic p-4 bg-slate-50 rounded-lg">
                      No restrictions. Click edit to add some.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Utilities Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="w-5 h-5" />
                  Utilities
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium mb-2 block flex items-center gap-2">
                      <Zap className="w-4 h-4" /> Electricity Supply
                    </Label>
                    <Input
                      placeholder="e.g. Mains electricity"
                      value={utilities.electricity_supply}
                      onChange={(e) => setUtilities({ ...utilities, electricity_supply: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium mb-2 block flex items-center gap-2">
                      <Flame className="w-4 h-4" /> Heating
                    </Label>
                    <Input
                      placeholder="e.g. Gas central heating"
                      value={utilities.heating}
                      onChange={(e) => setUtilities({ ...utilities, heating: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium mb-2 block flex items-center gap-2">
                      <Droplets className="w-4 h-4" /> Water Supply
                    </Label>
                    <Input
                      placeholder="e.g. Mains water"
                      value={utilities.water_supply}
                      onChange={(e) => setUtilities({ ...utilities, water_supply: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium mb-2 block">Sewerage</Label>
                    <Input
                      placeholder="e.g. Mains sewerage"
                      value={utilities.sewerage}
                      onChange={(e) => setUtilities({ ...utilities, sewerage: e.target.value })}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-sm font-medium mb-2 block flex items-center gap-2">
                      <Wifi className="w-4 h-4" /> Broadband
                    </Label>
                    <Input
                      placeholder="e.g. Fibre available, up to 100Mbps"
                      value={utilities.broadband}
                      onChange={(e) => setUtilities({ ...utilities, broadband: e.target.value })}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Marketing Alerts Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Send className="w-5 h-5" />
                  Marketing Alerts
                </CardTitle>
                <CardDescription>Send marketing alerts for this property below.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {MARKETING_ALERT_TYPES.map((alert) => (
                    <div key={alert.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <p className="font-medium">{alert.label}</p>
                        <p className="text-sm text-muted-foreground">{alert.description}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="bg-purple-100 text-purple-700 hover:bg-purple-200"
                          onClick={() => handleSendAlert(alert.id, "email")}
                        >
                          <Mail className="w-4 h-4 mr-1" />
                          EMAIL
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="bg-purple-100 text-purple-700 hover:bg-purple-200"
                          onClick={() => handleSendAlert(alert.id, "sms")}
                        >
                          <MessageSquare className="w-4 h-4 mr-1" />
                          SMS
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Realtime Portals Card */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="w-5 h-5" />
                    Realtime Portals
                  </CardTitle>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="bg-red-100 text-red-700">
                      REMOVE ALL
                    </Button>
                    <Button variant="outline" size="sm" className="bg-purple-100 text-purple-700">
                      UPDATE ALL
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left p-3 text-sm font-medium text-purple-700">Portal</th>
                        <th className="text-left p-3 text-sm font-medium text-purple-700">Link</th>
                        <th className="text-left p-3 text-sm font-medium text-purple-700">Account ID</th>
                        <th className="text-left p-3 text-sm font-medium text-purple-700">Account</th>
                        <th className="text-right p-3 text-sm font-medium"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {portalAccounts.map((account, index) => {
                        const portal = PORTALS.find(p => p.id === account.portal);
                        return (
                          <tr key={account.portal} className="border-t">
                            <td className="p-3">
                              <div className="flex items-center gap-2">
                                <span className="text-xl">{portal?.logo}</span>
                                <span className="font-semibold">{portal?.name}</span>
                              </div>
                            </td>
                            <td className="p-3">
                              {account.link ? (
                                <a href={account.link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                  View
                                </a>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </td>
                            <td className="p-3">
                              <Input
                                placeholder="Account ID"
                                value={account.account_id}
                                onChange={(e) => {
                                  const updated = [...portalAccounts];
                                  updated[index].account_id = e.target.value;
                                  setPortalAccounts(updated);
                                }}
                                className="w-32"
                              />
                            </td>
                            <td className="p-3 text-muted-foreground">{account.account_name}</td>
                            <td className="p-3 text-right">
                              <div className="flex gap-2 justify-end">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="bg-red-100 text-red-700 hover:bg-red-200"
                                  onClick={() => handlePortalRemove(account.portal)}
                                >
                                  REMOVE
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="bg-purple-100 text-purple-700 hover:bg-purple-200"
                                  onClick={() => handlePortalUpdate(account.portal)}
                                >
                                  UPDATE
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Sidebar - Property Menu */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24">
              <CardHeader className="pb-3">
                <CardTitle className="text-purple-700 text-base">Property menu</CardTitle>
              </CardHeader>
              <CardContent className="space-y-1">
                {[
                  { label: "Dashboard", href: `/sales/case/${caseId}` },
                  { label: "Property Details", href: `/sales/case/${caseId}/documents` },
                  { label: "Marketing Details", href: `/sales/case/${caseId}/marketing`, active: true },
                  { label: "Photos", href: `/sales/case/${caseId}/documents?tab=photos` },
                  { label: "Floorplans", href: `/sales/case/${caseId}/documents?tab=floorplans` },
                  { label: "EPC", href: `/sales/case/${caseId}/documents?tab=epc` },
                  { label: "IDs", href: `/sales/case/${caseId}/documents?tab=ids` },
                  { label: "Brochures", href: `/sales/case/${caseId}/brochure` },
                  { label: "Documents", href: `/sales/case/${caseId}/documents` },
                ].map((item) => (
                  <button
                    key={item.label}
                    onClick={() => item.href !== "#" && navigate(item.href)}
                    className={`w-full text-left px-3 py-2 text-sm rounded transition-colors ${
                      item.active 
                        ? "text-purple-700 font-medium" 
                        : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </CardContent>

              <CardHeader className="pb-3 pt-6">
                <CardTitle className="text-purple-700 text-base">Sale menu</CardTitle>
              </CardHeader>
              <CardContent className="space-y-1">
                {[
                  { label: "Viewings", href: `/sales/case/${caseId}/viewings` },
                  { label: "Offers", href: `/sales/case/${caseId}/offers` },
                  { label: "Sale Progression", href: `/sales/case/${caseId}` },
                ].map((item) => (
                  <button
                    key={item.label}
                    onClick={() => item.href !== "#" && navigate(item.href)}
                    className="w-full text-left px-3 py-2 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded transition-colors"
                  >
                    {item.label}
                  </button>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default MarketingDetailsPage;
